import { api } from './api';

export interface Survey {
  id: string;
  title: string;
  description?: string;
  status: 'DRAFT' | 'PUBLISHED' | 'CLOSED' | 'ARCHIVED';
  isAnonymous: boolean;
  maxResponses?: number;
  allowMultiple: boolean;
  xpReward?: number;
  productId?: string;
  moduleId?: string;
  startsAt?: string;
  endsAt?: string;
  createdAt: string;
  updatedAt: string;
  product?: { id: string; title: string };
  questions?: SurveyQuestion[];
  triggers?: SurveyTrigger[];
  _count?: { questions: number; responses: number; triggers: number };
}

export interface SurveyQuestion {
  id: string;
  surveyId: string;
  type: string;
  label: string;
  description?: string;
  placeholder?: string;
  required: boolean;
  order: number;
  config?: any;
  section?: string;
  conditionQuestionId?: string;
  conditionValue?: string;
}

export interface SurveyTrigger {
  id: string;
  surveyId: string;
  type: string;
  config?: any;
  isActive: boolean;
}

export interface SurveyResponse {
  id: string;
  surveyId: string;
  userId?: string;
  triggerType?: string;
  completedAt?: string;
  createdAt: string;
  survey?: { id: string; title: string; description?: string };
  user?: { id: string; name: string; email: string; avatar?: string };
  answers: SurveyAnswer[];
}

export interface SurveyAnswer {
  id: string;
  questionId: string;
  value: string;
  question?: { id: string; label: string; type: string };
}

export const surveysService = {
  // Admin
  async findAll(params?: {
    page?: number;
    perPage?: number;
    status?: string;
    productId?: string;
  }) {
    const p = new URLSearchParams();
    if (params?.page) p.set('page', String(params.page));
    if (params?.perPage) p.set('perPage', String(params.perPage));
    if (params?.status) p.set('status', params.status);
    if (params?.productId) p.set('productId', params.productId);
    const { data } = await api.get(`/surveys?${p}`);
    // TransformInterceptor flattens paginated responses:
    // { success, data: Survey[], meta } (NOT { data: { data, meta } })
    const items = data?.data || data;
    const meta = data?.meta || {
      total: 0,
      page: 1,
      perPage: 20,
      totalPages: 1,
    };
    return {
      data: Array.isArray(items) ? items : items?.data || [],
      meta,
    } as {
      data: Survey[];
      meta: {
        total: number;
        page: number;
        perPage: number;
        totalPages: number;
      };
    };
  },

  async findById(id: string) {
    const { data } = await api.get(`/surveys/admin/${id}`);
    return (data?.data || data) as Survey;
  },

  async create(dto: Partial<Survey> & { questions?: any[]; triggers?: any[] }) {
    const { data } = await api.post('/surveys', dto);
    return (data?.data || data) as Survey;
  },

  async update(id: string, dto: Partial<Survey>) {
    const { data } = await api.put(`/surveys/${id}`, dto);
    return (data?.data || data) as Survey;
  },

  async delete(id: string) {
    return api.delete(`/surveys/${id}`);
  },

  async duplicate(id: string) {
    const { data } = await api.post(`/surveys/${id}/duplicate`);
    return (data?.data || data) as Survey;
  },

  // Questions
  async addQuestion(surveyId: string, q: Partial<SurveyQuestion>) {
    const { data } = await api.post(`/surveys/${surveyId}/questions`, q);
    return (data?.data || data) as SurveyQuestion;
  },

  async updateQuestion(questionId: string, q: Partial<SurveyQuestion>) {
    const { data } = await api.put(`/surveys/questions/${questionId}`, q);
    return (data?.data || data) as SurveyQuestion;
  },

  async deleteQuestion(questionId: string) {
    return api.delete(`/surveys/questions/${questionId}`);
  },

  async reorderQuestions(surveyId: string, orderedIds: string[]) {
    return api.put(`/surveys/${surveyId}/questions/reorder`, { orderedIds });
  },

  // Triggers
  async addTrigger(surveyId: string, t: { type: string; config?: any }) {
    const { data } = await api.post(`/surveys/${surveyId}/triggers`, t);
    return (data?.data || data) as SurveyTrigger;
  },

  async deleteTrigger(triggerId: string) {
    return api.delete(`/surveys/triggers/${triggerId}`);
  },

  // Responses (admin)
  async getResponses(
    surveyId: string,
    params?: { page?: number; perPage?: number; userId?: string },
  ) {
    const p = new URLSearchParams();
    if (params?.page) p.set('page', String(params.page));
    if (params?.perPage) p.set('perPage', String(params.perPage));
    if (params?.userId) p.set('userId', params.userId);
    const { data } = await api.get(`/surveys/${surveyId}/responses?${p}`);
    const items = data?.data || data;
    const meta = data?.meta || {
      total: 0,
      page: 1,
      perPage: 20,
      totalPages: 1,
    };
    return {
      data: Array.isArray(items) ? items : items?.data || [],
      meta,
    } as {
      data: SurveyResponse[];
      meta: {
        total: number;
        page: number;
        perPage: number;
        totalPages: number;
      };
    };
  },

  async getStats(surveyId: string) {
    const { data } = await api.get(`/surveys/${surveyId}/stats`);
    return (data?.data || data) as any;
  },

  // Admin — user responses
  async getUserResponses(userId: string) {
    const { data } = await api.get(`/surveys/user/${userId}/responses`);
    const result = data?.data || data;
    return Array.isArray(result) ? result : result?.data || [];
  },

  // User
  async getMyPending() {
    const { data } = await api.get('/surveys/me/pending');
    return (data?.data || data) as Survey[];
  },

  async getMyResponses() {
    const { data } = await api.get('/surveys/me/responses');
    return (data?.data || data) as SurveyResponse[];
  },

  async respond(
    surveyId: string,
    answers: Array<{ questionId: string; value: string }>,
    triggerType?: string,
  ) {
    const { data } = await api.post(`/surveys/${surveyId}/respond`, {
      answers,
      triggerType,
    });
    return (data?.data || data) as SurveyResponse;
  },
};
